/**
 * usePresence Hook
 * Manages SignalR connection for real-time user presence tracking.
 * Sends periodic heartbeats and adjusts frequency based on tab visibility.
 * Stores state in Zustand so it's available app-wide.
 */
import { useCallback, useEffect, useRef } from 'react';
import { HubConnectionState } from '@microsoft/signalr';

import {
  createPresenceConnection,
  type PresenceConnection,
} from '@/services/signalr/presenceHub';
import { retryStart } from '@/services/signalr/retryPolicy';
import { apiClient } from '@/services/api/apiClient';
import { useGeoMarkupStore } from '@/store';
import { PRESENCE_CONFIG } from '@/constants/api';
import type { PresenceStatus } from '@/store/slices/presenceSlice';

/** Options for the usePresence hook */
interface UsePresenceOptions {
  /** Workspace ID to track presence for (null to disconnect) */
  workspaceId: string | null;
  /** Whether to enable presence tracking */
  enabled?: boolean;
}

/** Stable imperative senders exposed by the usePresence hook. */
interface UsePresenceResult {
  /** Broadcasts the current position to the active workspace. */
  sendLocation: (lat: number, lng: number, heading?: number) => void;
  /** Tells the server the user stopped sharing their location. */
  stopSharingLocation: () => void;
}

interface UserPresenceInfo {
  userId: string;
  status: string;
}

interface PresenceResponse {
  onlineUserIds: string[];
  users?: UserPresenceInfo[];
}

/**
 * Fetches the initial presence state for a workspace.
 * Errors are logged but not thrown to avoid breaking the hook.
 */
async function fetchInitialPresence(
  workspaceId: string
): Promise<PresenceResponse> {
  try {
    const response = await apiClient.get<PresenceResponse>(
      `/api/v1/workspaces/${workspaceId}/presence`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to fetch initial presence:', error);
    return { onlineUserIds: [] };
  }
}

function applyPresenceResponse(presenceData: PresenceResponse): void {
  const { setOnlineUsers, setUserStatuses } =
    useGeoMarkupStore.getState().presence;

  if (presenceData.users && presenceData.users.length > 0) {
    const statuses: Record<string, PresenceStatus> = {};
    for (const userInfo of presenceData.users) {
      statuses[userInfo.userId] =
        userInfo.status.toLowerCase() as PresenceStatus;
    }
    setUserStatuses(statuses);
  } else {
    setOnlineUsers(presenceData.onlineUserIds);
  }
}

/** A member's broadcast location as returned by the `GetWorkspaceLocations` hub method. */
interface WorkspaceLocationEntry {
  userId: string;
  latitude: number | null;
  longitude: number | null;
  heading: number | null;
}

/**
 * Seeds the store with the live member locations already broadcast in a
 * workspace. Errors are logged but never thrown — location seeding is
 * best-effort and must not break the presence join.
 */
async function seedWorkspaceLocations(
  connection: PresenceConnection,
  targetWorkspaceId: string
): Promise<void> {
  try {
    const locations = await connection.invoke<WorkspaceLocationEntry[]>(
      'GetWorkspaceLocations',
      targetWorkspaceId
    );
    if (!Array.isArray(locations)) {
      return;
    }

    const { setMemberLocation } = useGeoMarkupStore.getState().presence;
    for (const entry of locations) {
      if (entry.latitude === null || entry.longitude === null) {
        continue;
      }
      setMemberLocation(entry.userId, {
        lat: entry.latitude,
        lng: entry.longitude,
        heading: entry.heading ?? undefined,
      });
    }
  } catch (error) {
    console.error('Failed to seed workspace locations:', error);
  }
}

async function joinWorkspaceAndFetch(
  connection: PresenceConnection,
  targetWorkspaceId: string
): Promise<void> {
  try {
    await connection.invoke('JoinWorkspace', targetWorkspaceId);
    const presenceData = await fetchInitialPresence(targetWorkspaceId);
    applyPresenceResponse(presenceData);
    await seedWorkspaceLocations(connection, targetWorkspaceId);
  } catch (error) {
    console.error('Failed to join workspace for presence:', error);
  }
}

/**
 * Hook for managing SignalR connection to presence hub.
 * Tracks which users are currently online/away in a workspace.
 * Sends heartbeats and adjusts interval based on tab visibility.
 * State is stored in Zustand store for app-wide access.
 *
 * @example
 * // Call once at app level and capture the imperative location senders:
 * const { sendLocation, stopSharingLocation } = usePresence({
 *   workspaceId: currentWorkspaceId,
 *   enabled: !!currentWorkspaceId,
 * });
 *
 * // Read from store anywhere:
 * const userStatuses = useGeoMarkupStore(state => state.presence.userStatuses);
 */
export function usePresence(options: UsePresenceOptions): UsePresenceResult {
  const { workspaceId, enabled = true } = options;

  const connectionRef = useRef<PresenceConnection | null>(null);
  const currentWorkspaceIdRef = useRef<string | null>(null);

  // Fire-and-forget a hub method, but only when the connection is live and a
  // workspace is joined. Reads connection/workspace from refs so the senders
  // built on top stay referentially stable. The workspace id is passed as the
  // first argument to the hub method.
  const invokeWhenConnected = useCallback(
    (method: string, ...args: unknown[]): void => {
      const connection = connectionRef.current;
      const workspaceId = currentWorkspaceIdRef.current;
      if (
        connection &&
        connection.state === HubConnectionState.Connected &&
        workspaceId
      ) {
        connection.invoke(method, workspaceId, ...args).catch(() => {});
      }
    },
    []
  );

  // Stable imperative senders. They delegate to `invokeWhenConnected` (which
  // reads from refs) so the returned callbacks stay referentially stable.
  const sendLocation = useCallback(
    (lat: number, lng: number, heading?: number) => {
      invokeWhenConnected('UpdateLocation', lat, lng, heading ?? null);
    },
    [invokeWhenConnected]
  );

  const stopSharingLocation = useCallback(() => {
    invokeWhenConnected('StopSharingLocation');
  }, [invokeWhenConnected]);

  // Setup connection and event handlers
  useEffect(() => {
    if (!enabled) {
      return;
    }

    let aborted = false;
    const {
      addOnlineUser,
      removeOnlineUser,
      setPresenceConnected,
      setOnlineUsers,
      setUserStatus,
      setMemberLocation,
      removeMemberLocation,
      clearMemberLocations,
    } = useGeoMarkupStore.getState().presence;

    const connection = createPresenceConnection();
    connectionRef.current = connection;

    // Register presence event handlers
    connection.on('UserOnline', (userId: string) => {
      addOnlineUser(userId);
    });

    connection.on('UserOffline', (userId: string) => {
      removeOnlineUser(userId);
      removeMemberLocation(userId);
    });

    connection.on('UserStatusChanged', (userId: string, status: string) => {
      setUserStatus(userId, status.toLowerCase() as PresenceStatus);
    });

    connection.on(
      'UserLocationChanged',
      (userId: string, lat: number, lng: number, heading?: number | null) =>
        setMemberLocation(userId, { lat, lng, heading: heading ?? undefined })
    );

    connection.on('UserLocationCleared', (userId: string) =>
      removeMemberLocation(userId)
    );

    // Connection state handlers
    connection.onreconnecting(() => {
      setPresenceConnected(false);
    });

    connection.onreconnected(() => {
      setPresenceConnected(true);

      // Rejoin current workspace and re-fetch presence after reconnection
      if (currentWorkspaceIdRef.current) {
        joinWorkspaceAndFetch(connection, currentWorkspaceIdRef.current);
      }
    });

    function handleConnected(): void {
      setPresenceConnected(true);
      startHeartbeat();
      if (currentWorkspaceIdRef.current) {
        joinWorkspaceAndFetch(connection, currentWorkspaceIdRef.current);
      }
    }

    function scheduleRetry(): void {
      retryStart(
        () => connection.start(),
        () => aborted,
        handleConnected
      );
    }

    connection.onclose(() => {
      setPresenceConnected(false);
      if (!aborted) scheduleRetry();
    });

    // Heartbeat management
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let isTabVisible = !document.hidden;

    function sendHeartbeat(): void {
      if (
        currentWorkspaceIdRef.current &&
        connection.state === HubConnectionState.Connected
      ) {
        connection
          .invoke('Heartbeat', currentWorkspaceIdRef.current)
          .catch(() => {});
      }
    }

    function stopHeartbeat(): void {
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    }

    function startHeartbeat(): void {
      stopHeartbeat();
      const interval = isTabVisible
        ? PRESENCE_CONFIG.HEARTBEAT_VISIBLE_INTERVAL
        : PRESENCE_CONFIG.HEARTBEAT_HIDDEN_INTERVAL;
      heartbeatTimer = setInterval(sendHeartbeat, interval);
    }

    function handleVisibilityChange(): void {
      isTabVisible = !document.hidden;
      sendHeartbeat();
      startHeartbeat();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start connection and join initial workspace
    connection
      .start()
      .then(() => {
        if (aborted) return;
        setPresenceConnected(true);
        startHeartbeat();

        // Join workspace that was set before connection was ready
        if (currentWorkspaceIdRef.current) {
          joinWorkspaceAndFetch(connection, currentWorkspaceIdRef.current);
        }
      })
      .catch((error) => {
        console.error('Failed to start presence connection:', error);
        scheduleRetry();
      });

    // Cleanup on unmount or when disabled
    return () => {
      aborted = true;
      stopHeartbeat();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      connection.stop();
      connectionRef.current = null;
      setPresenceConnected(false);
      setOnlineUsers([]);
      clearMemberLocations();
    };
  }, [enabled]);

  // Join/Leave workspace when workspaceId changes
  useEffect(() => {
    const previousWorkspaceId = currentWorkspaceIdRef.current;
    currentWorkspaceIdRef.current = workspaceId;

    const connection = connectionRef.current;
    if (!connection || connection.state !== HubConnectionState.Connected) {
      // Connection not ready yet; the start().then() callback will join
      return;
    }

    // Leave previous workspace
    if (previousWorkspaceId && previousWorkspaceId !== workspaceId) {
      connection
        .invoke('LeaveWorkspace', previousWorkspaceId)
        .catch((error) =>
          console.error('Failed to leave workspace for presence:', error)
        );
    }

    // Join new workspace and fetch initial state
    if (workspaceId) {
      joinWorkspaceAndFetch(connection, workspaceId);
    } else {
      useGeoMarkupStore.getState().presence.setOnlineUsers([]);
    }
  }, [workspaceId]);

  return { sendLocation, stopSharingLocation };
}
